import { Button } from "@bowfast/ui/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@bowfast/ui/components/popover";
import { Link } from "@tanstack/react-router";
import { BellIcon } from "lucide-react";
import { useNotifications } from "~/lib/notifications-context";
import { getSignalConfig } from "~/pages/notifications/notifications.config";
import { formatTime } from "~/pages/notifications/notifications.utils";

/**
 * App-header notifications tray.
 * Reads from NotificationsProvider (shared query + subscription).
 * Shows up to 5 most-recent signals; clicking an unread row marks it read.
 */
export function NotificationsTray() {
  const { signals, unreadCount, handleMarkRead } = useNotifications();
  const now = new Date();
  const preview = signals.slice(0, 5);

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ""}`}
            className="relative"
          />
        }
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-1 right-1 size-2.5 rounded-full bg-coral-500 ring-2 ring-background"
          />
        )}
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={4} className="w-80 p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
          <span className="text-sm font-semibold font-heading">Signals</span>
          {unreadCount > 0 && (
            <span className="text-[0.625rem] font-bold uppercase tracking-widest text-muted-foreground">
              {unreadCount} unread
            </span>
          )}
        </div>

        {/* Signal list */}
        {preview.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">All quiet on deck</div>
        ) : (
          <ul className="divide-y divide-border">
            {preview.map((signal) => {
              const cfg = getSignalConfig(signal.type);
              const Icon = cfg.Icon;
              const isUnread = !signal.readAt;
              return (
                <li key={signal.id}>
                  <button
                    type="button"
                    className="flex w-full gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => {
                      if (isUnread) handleMarkRead(signal.id);
                    }}
                  >
                    <div className={`mt-0.5 shrink-0 ${cfg.iconColor}`}>
                      <Icon className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-tight text-foreground truncate">{signal.title}</p>
                      <p className="text-xs text-muted-foreground leading-snug mt-0.5 line-clamp-1">{signal.body}</p>
                      <p className="text-[0.625rem] text-muted-foreground/70 mt-1 uppercase tracking-widest">
                        {formatTime(signal.createdAt, now)}
                      </p>
                    </div>
                    {isUnread && (
                      <span aria-hidden="true" className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* Footer */}
        <div className="border-t border-border px-3 py-2.5">
          <Link to="/notifications" className="text-xs font-medium text-primary hover:underline underline-offset-2">
            View all signals →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
